// CSoC (Caissa School of Chess) lead capture for the OTP-gated assessment flow.
//
// The assessment CTA on the CircleChess CSoC landing pages links here
// (learn.circlechess.com/evaluateLogin?...&csoc_lead=1&utm_*=...&landing_page=...).
// Because mobile/OTP is mandatory, every completed assessment is a qualified
// lead — so we mirror the landing-page Enquire flow's two webhooks:
//   send_otp succeeds   -> csoc_callback   (capture the lead)
//   verify_otp succeeds -> create_ticket   (mark it OTP-verified)
//
// These fire ONLY when the visitor arrived from a CSoC page, detected via the
// `csoc_lead=1` marker the landing-page CTA adds. Normal / admin logins never
// hit these endpoints. Both calls are cross-origin (api.circlechess.com, no CORS
// headers) fire-and-forget: `no-cors`, never read, never throw, never block.

import Environment from '../../environment';

// The lead webhooks live on the chess API host (api.circlechess.com in prod) —
// the same base ChessRestClient uses.
const CSOC_LEAD_API_BASE = Environment.CHESS_API_URL;

/** Strip everything but digits (so "+91" -> "91"). */
function bareDigits(value: string): string {
  return (value || '').replace(/[^\d]/g, '');
}

function getCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const match = document.cookie.match(
    new RegExp('(^|;\\s*)' + name + '=([^;]+)'),
  );
  return match ? decodeURIComponent(match[2]) : null;
}

function urlParams(): Record<string, string> {
  if (typeof window === 'undefined') {
    return {};
  }
  return Object.fromEntries(new URLSearchParams(window.location.search));
}

/**
 * True only when the visitor arrived from a Caissa School of Chess page — the
 * CTA there stamps `csoc_lead=1`. Read straight from the URL (the assessment
 * send->verify happens on one page load, so the query string is stable), which
 * also means a later ordinary login on the same browser never triggers a lead.
 */
export function isCsocAssessmentLead(): boolean {
  return urlParams().csoc_lead === '1';
}

/**
 * csoc_callback — capture the lead. Elementor-webhook shape (form-urlencoded,
 * fields looked up by TITLE on the backend), matching the working landing-page
 * flow. Assessment leads carry phone + attribution only; name/email are blank.
 */
export function submitCsocCallback(opts: {
  fullPhone: string;
  countryCode: string;
  email?: string;
  name?: string;
}): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const params = urlParams();

    // Forward only UTM / click params actually present — the backend lowercases
    // utm values and would crash on a null, so never emit empty keys.
    const utmObj: Record<string, string> = {};
    const utmKeys = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_content',
      'utm_term',
      'gclid',
      'fbclid',
      'wbraid',
      'gbraid',
    ];
    for (const k of utmKeys) {
      if (params[k]) {
        utmObj[k] = params[k];
      }
    }
    const fbp = getCookie('_fbp');
    if (fbp) {
      utmObj.fbp = fbp;
    }
    const fbc = getCookie('_fbc');
    if (fbc) {
      utmObj.fbc = fbc;
    }
    utmObj.page_url = window.location.href;
    // The originating CSoC page (e.g. /caissa-school-of-chess-usa), passed
    // through from the landing-page CTA.
    utmObj.landing_page = params.landing_page || window.location.href;
    const ref = document.referrer;
    if (ref) {
      utmObj.referrer = ref;
    }

    // `location` (e.g. IND / USA / AUSTRALIA — the config `location`, NOT the 2-letter
    // ISO) flows into the ticket subject, from which cc_customer_lead_source.country
    // is generated. Sending 'IN' produced country='Other'; sending 'IND' → 'IND'.
    const location = params.country || bareDigits(opts.countryCode) || '';
    const type = 'Assessment - CSoC Page';
    const phoneWithCode = opts.fullPhone.startsWith('+')
      ? opts.fullPhone
      : `+${opts.fullPhone}`;

    // The assessment collects no name and no email, so BOTH are left blank — the
    // lead is identified purely by phone. No synthesized <phone>@circlechess.cc.
    // (Requires the backend dedupe fix so an empty email is not a match key,
    // otherwise Q(email__iexact='') collides with other empty-email leads and the
    // insert is skipped.)
    const name = opts.name || '';
    const email = opts.email || '';

    const body = new URLSearchParams();
    body.append('type', type);
    body.append('form_name', type);
    body.append('form_id', 'csoc');

    const fields: Array<[string, string, string]> = [
      ['name', 'Name', name],
      ['email', 'Email', email],
      ['Phone', 'Phone', phoneWithCode],
      ['phone_number', 'Phone Number', opts.fullPhone],
      ['availability', 'Availability', 'Any time'],
      ['location', 'Location', location],
      ['UTM', 'UTM', JSON.stringify(utmObj)],
    ];
    for (const [id, title, value] of fields) {
      body.append(`fields[${id}][id]`, id);
      body.append(`fields[${id}][title]`, title);
      body.append(`fields[${id}][value]`, value ?? '');
      body.append(`fields[${id}][raw_value]`, value ?? '');
      body.append(`form_fields[${id}]`, value ?? '');
    }

    fetch(`${CSOC_LEAD_API_BASE}/csoc_callback`, {
      method: 'POST',
      body, // URLSearchParams => application/x-www-form-urlencoded (CORS-safelisted)
      mode: 'no-cors',
      keepalive: true,
    }).catch(err => console.error('csoc_callback failed:', err));
  } catch (err) {
    console.error('csoc_callback failed:', err);
  }
}

/**
 * create_ticket — called AFTER a successful OTP verification to mark the lead
 * verified. Fire-and-forget JSON: { email, phone, otp_verified: true }. No
 * "application/json" header — that would force a CORS preflight the endpoint
 * rejects; under no-cors the body is sent as text/plain, which the server
 * still parses.
 */
export function createTicket(opts: {email?: string; phone: string}): void {
  if (typeof window === 'undefined') {
    return;
  }
  fetch(`${CSOC_LEAD_API_BASE}/create_ticket`, {
    method: 'POST',
    body: JSON.stringify({
      email: opts.email || '',
      phone: opts.phone,
      otp_verified: true,
    }),
    mode: 'no-cors',
    keepalive: true,
  }).catch(err => console.error('create_ticket failed:', err));
}
